/* eslint-disable no-underscore-dangle */
/* eslint-disable react/prop-types */
// import { PropTypes } from "prop-types";

import React from "react";
import { FacebookShareButton, LinkedinShareButton } from "react-share";
import { graphql } from "gatsby";
import moment from "moment";
import tw, { css } from "twin.macro";
import PortableText from "~sanity/PortableText.jsx";
import { Go, Layout, LineSwipe, SEO, WaveCanvas } from "~components";
import Grid from "~styles/Grid.jsx";
import { EASING_CUBIC } from "~styles/Animations.jsx";
import * as T from "~styles/Typography.jsx";
import { MEDIA_QUERIES } from "~utils/css";
import { getColor } from "~utils/helpers";

import { ReactComponent as ArrowReset } from "~assets/svg/arrow-reset.svg";
import { ReactComponent as ArrowRight } from "~assets/svg/arrow-right.svg";
import { ReactComponent as IconFacebook } from "~assets/svg/facebook.svg";
import { ReactComponent as IconLinkedin } from "~assets/svg/linkedin.svg";

const Article = ({ data, location }) => {
  // ===========================================================================
  // variables

  const cms = data.sanityArticle;

  const formattedDate = moment(cms?.date).format(`LL`);

  const links = cms?.relatedArticles;

  if (links?.length > 3) {
    links.length = 2;
  }

  // ===========================================================================
  // render

  if (typeof window === `undefined`) {
    return <></>;
  }

  return (
    <>
      <SEO
        customTitle={cms?.title}
        customDescription={cms?.seoDescription}
        customKeywords={cms?.seoKeywords}
        path={location.pathname}
      />

      <Layout _css={[tw`relative pt-64`]}>
        <div
          css={[
            tw`w-screen h-screen fixed top-0 right-0 bottom-0 left-0 pointer-events-none`
          ]}
        >
          <WaveCanvas />
        </div>

        <Grid _css={[tw`z-10`]}>
          <header css={[tw`col-span-12 relative flex items-center`]}>
            <T.Head
              _css={[
                css`
                  color: ${cms?.tag?.hex};
                `
              ]}
              font="5"
            >
              {cms?.tag?.title}
            </T.Head>

            <div css={[tw`w-32 relative block ml-5`]} color="text-blue-med">
              <LineSwipe color="#0008" delay={500} duration={2000} />
            </div>
          </header>

          <div
            css={[
              css`
                color: ${cms?.tag?.hex};
              `,
              tw`col-span-7 relative mt-16 mb-24`
            ]}
          >
            <T.Head _css={[tw`font-bold`]} font="1" level="1">
              {cms?.title}
            </T.Head>
          </div>

          <section
            css={[
              css`
                //
              `,
              tw`col-span-12 relative flex items-center justify-between pt-3 border-t`
            ]}
          >
            <p css={[tw`font-head`]}>
              <span
                css={[
                  css`
                    padding-right: 0.3rem;
                  `
                ]}
              >
                By
              </span>
              <span css={[tw`font-bold`]}>{cms?.author}</span>
            </p>

            <p css={[tw`font-head`]}>{formattedDate}</p>
          </section>

          <div css={[tw`col-span-12 relative mt-12 mb-12`]} />

          <div css={[tw`col-span-3 relative`]}>
            <T.Body _css={[tw`font-bold`]} font="h6">
              Share
            </T.Body>

            <div css={[tw`mt-6 block`]}>
              <FacebookShareButton
                url={window.location.href}
                quote={cms?.excerpt || cms?.title}
              >
                <IconFacebook
                  fill="black"
                  css={[
                    css`
                      circle {
                        fill: ${getColor(`grey`)};
                      }

                      ${MEDIA_QUERIES.hoverable} {
                        &:hover {
                          circle {
                            fill: ${getColor(`blue-turq`)};
                          }
                          path {
                            fill: ${getColor(`white`)};
                          }
                        }
                      }
                    `,
                    tw`w-12`
                  ]}
                />
              </FacebookShareButton>
            </div>

            <div css={[tw`mt-3 block`]}>
              <LinkedinShareButton
                title={cms?.title}
                summary={cms?.excerpt || `Royal Hospital for Women`}
                url={window.location.href}
              >
                <IconLinkedin
                  fill="black"
                  css={[
                    css`
                      circle {
                        fill: ${getColor(`grey`)};
                      }

                      ${MEDIA_QUERIES.hoverable} {
                        &:hover {
                          circle {
                            fill: ${getColor(`blue-turq`)};
                          }
                          path {
                            fill: ${getColor(`white`)};
                          }
                        }
                      }
                    `,
                    tw`w-12`
                  ]}
                />
              </LinkedinShareButton>
            </div>
          </div>

          <div
            css={[
              css`
                a:not(.article-button) {
                  text-decoration: underline;
                }
              `,
              tw`col-span-8 relative`
            ]}
          >
            {(cms?._rawDataBody && (
              <PortableText blocks={cms?._rawDataBody || cms?._rawBody} />
            )) || (
              <>
                {cms?._rawBody && (
                  <PortableText blocks={cms?._rawDataBody || cms?._rawBody} />
                )}
              </>
            )}
          </div>
        </Grid>

        {/* // */}

        {links?.[0] && (
          <div
            css={[
              css`
                //
              `,
              tw`w-full relative mt-32 pt-20 md:pt-20 pb-10 md:pb-20 bg-blue-faint text-blue-deep`
            ]}
          >
            <Grid>
              <header
                css={[tw`col-span-12 md:col-span-4 relative flex items-start`]}
              >
                <div css={[tw`relative`]}>
                  <T.Head
                    _css={[
                      css`
                        transform: translateY(-0.9rem);
                      `,
                      tw``
                    ]}
                    font="4"
                  >
                    Explore more
                  </T.Head>
                </div>

                <div css={[tw`w-32 relative md:mt-1 mb-16 md:mb-0 ml-6`]}>
                  <LineSwipe color="#84bbd9" delay={500} duration={2000} />
                </div>
              </header>

              {/* // */}

              {links.map((link) => (
                <article
                  key={link.slug.current}
                  css={[css``, tw`col-span-12 md:col-span-4 relative md:px-3`]}
                >
                  <Go
                    to={link.slug.current}
                    _css={[
                      css`
                        p,
                        svg {
                          transition: transform 0.3s ${EASING_CUBIC};
                        }

                        &:hover {
                          background: rgba(255, 255, 255, 0.125);

                          p {
                            transform: translate3d(0.75rem, 0, 0);
                          }
                          svg {
                            transform: translate3d(-0.75rem, 0, 0);
                          }
                        }
                      `,
                      tw`w-full h-full relative block mb-6 md:mb-0 pt-4 md:pt-5 pb-4 md:pb-5 flex items-start md:items-center justify-between border-t border-black text-black`
                    ]}
                  >
                    <T.Body _css={[tw`font-bold`]} font="2">
                      {link.title}
                    </T.Body>

                    <ArrowRight css={[tw`w-6`]} fill={getColor(`blue-deep`)} />
                  </Go>
                </article>
              ))}
            </Grid>
          </div>
        )}

        <div
          css={[
            css`
              //
            `,
            tw`w-full relative pt-20 md:pt-16 pb-10 md:pb-20 bg-grey text-blue-deep`
          ]}
        >
          <Grid _css={[tw`items-center`]}>
            <header
              css={[tw`col-span-12 md:col-span-8 relative flex items-start`]}
            >
              <div css={[tw`relative`]}>
                <T.Head
                  _css={[
                    css`
                      transform: translateY(-0.9rem);
                    `,
                    tw``
                  ]}
                  font="4"
                >
                  Return to FRC research
                </T.Head>
              </div>

              <div css={[tw`w-32 relative md:mt-1 ml-6`]}>
                <LineSwipe color="#84bbd9" delay={500} duration={2000} />
              </div>
            </header>

            {/* // */}

            <article
              css={[
                css``,
                tw`col-span-12 md:col-span-4 relative md:-mt-5 md:px-3 flex items-center justify-end`
              ]}
            >
              <Go to="/frc-research">
                <button
                  type="button"
                  css={[
                    css`
                      ${MEDIA_QUERIES.hoverable} {
                        &:hover {
                          background: ${getColor(`blue-turq`)};
                          color: white;

                          svg {
                            fill: white;
                          }
                        }
                      }
                    `,
                    tw`w-48 h-12 relative block px-6 bg-white border text-black`
                  ]}
                >
                  <div
                    css={[
                      tw`w-full relative flex items-center justify-between`
                    ]}
                  >
                    <T.Body _css={[tw`font-bold`]} font="3">
                      Return
                    </T.Body>

                    <ArrowReset css={[tw`w-4`]} />
                  </div>
                </button>
              </Go>
            </article>
          </Grid>
        </div>
      </Layout>
    </>
  );
};

export default Article;

export const query = graphql`
  query Article($id: String!) {
    sanityArticle(id: { eq: $id }) {
      slug {
        current
      }

      id
      title
      author
      date
      excerpt

      _rawBody

      cardImage {
        asset {
          gatsbyImageData(width: 1024, placeholder: NONE)
        }
      }

      relatedArticles {
        title

        slug {
          current
        }
      }

      tag {
        title
        hex
      }

      seoDescription
      seoKeywords
    }
  }
`;
